import { MutationUpdaterFn } from "@apollo/client";
import { Dialog } from "@headlessui/react";
import { Formik } from "formik";
import { FormikHelpers, FormikProps } from "formik/dist/types";
import { useTranslation } from "next-i18next";
import React, { useMemo } from "react";
import * as yup from "yup";

import { DialogCloseButton } from "@/features/common/components/DialogCloseButton";
import { OutlinedButton } from "@/features/common/components/button/OutlinedButton";
import { TextAreaAutoResizeInputField } from "@/features/common/components/formik/TextAreaAutoResizeInputField";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { useHandleGraphqlError } from "@/features/common/hooks/useHandleGraphqlError";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  ReportPostInput,
  ReportPostMutation,
  useReportPostMutation,
} from "@/generated/generated-hooks";

type ReportPostInfo = {
  _id: string;
  title: string;
  writerId: string;
  slug: string;
};

export type ReportPostDialogViewProps = {
  post: ReportPostInfo;
  onClose: () => void;
};

const ReportPostDialogView: React.FC<ReportPostDialogViewProps> = (props) => {
  const { onClose, post } = props;

  const { user } = useUserContext();
  const { t } = useTranslation("common");
  const [reportPost, { data, error, loading }] = useReportPostMutation({
    update: reportPostUpdateFn(post?._id),
  });
  const handleGraphqlError = useHandleGraphqlError();

  const { openSnackbar } = useSnackbar();

  const validationSchema = useMemo(() => {
    const exceedWordLimitMessage = (maxLength: number) =>
      t("common:formValidation.required", { maxLength });
    return yup.object().shape({
      content: yup
        .string()
        .test("len", exceedWordLimitMessage(40), (val = "") => !!val.trim()),
    });
  }, [t]);

  const onSubmit = async (values, action: FormikHelpers<any>) => {
    try {
      await reportPost({
        variables: { input: { ...values, content: values.content.trim() } },
      });
      openSnackbar({
        message: t("common:post.report.success"),
        severity: "success",
      });
      onClose();
    } catch (error) {
      handleGraphqlError({
        e: error,
        t,
        defaultErrorTranslationKey: "common:post.report.fail",
        sentryCaptureContext: {
          extra: {
            post: post?._id,
          },
        },
      });
    }
  };

  const onCancelHandler = (confirmCancel: boolean) => {
    if (confirmCancel) return;
    onClose();
  };

  const initialValues = useMemo(() => {
    if (!post || !user?._id) return null;
    return getReportPostInitialValues(post, user._id);
  }, [post, user?._id]);
  return (
    <div className="p-3 md:p-6 space-y-4">
      <Dialog.Title
        as="h3"
        className="flex justify-between text-xl  text-gray-900"
      >
        {t("common:post.report.title", { title: post?.title })}
        <DialogCloseButton onClick={onClose} />
      </Dialog.Title>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {(props: FormikProps<ReportPostInput>) => {
          const { dirty, isValid, isSubmitting, handleSubmit } = props;
          return (
            <form>
              <div className="space-y-4">
                <div className="text-base  text-gray-900">
                  <TextAreaAutoResizeInputField
                    autoFocus={true}
                    className="border-black"
                    name="content"
                    rowsMin={5}
                    placeholder={t("common:post.report.placeholder")}
                  />
                </div>

                <div className="flex justify-end">
                  <OutlinedButton
                    variant="secondary"
                    buttonProps={{
                      onClick: () =>
                        onCancelHandler(
                          props.dirty &&
                            !window.confirm(
                              t("common:post.report.cancelAlert"),
                            ),
                        ),
                      disabled: loading,
                      type: "button",
                    }}
                  >
                    {t("common:cancel")}
                  </OutlinedButton>

                  <OutlinedButton
                    variant="primary"
                    utilityClassName="ml-3.5"
                    buttonProps={{
                      onClick: () => handleSubmit(),
                      type: "submit",
                      disabled: !dirty || !isValid || isSubmitting,
                    }}
                  >
                    {t("common:post.report.confirmButtonLabel")}
                  </OutlinedButton>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ReportPostDialogView;

const getReportPostInitialValues = (
  post: ReportPostInfo,
  reporterId: string,
): ReportPostInput => {
  const { _id, writerId } = post;
  return { postId: _id, reporterId, writerId, content: "" };
};

const reportPostUpdateFn: (
  postId: string,
) => MutationUpdaterFn<ReportPostMutation> =
  (postId) =>
  (cache, { data: { reportPost } }) => {
    if (!reportPost) return;
    cache.modify({
      id: cache.identify({ __typename: "Post", _id: postId }),
      fields: {
        hasCurrentUserReport() {
          return true;
        },
      },
    });
  };
